import React, { useRef, useState } from 'react'
import { KibButtonNew } from '@chewy/kib-controls-react'
import { KibField } from '@chewy/kib-fields-new-react'
import { useClickOutside } from '@chewy/kib-helpers-react'
import { KibChevronDownIcon } from '@chewy/kib-icons-react'
import KibTag from '@chewy/kib-tag-react'
import classNames from 'classnames'

import { ColorVariant, ColorVariantToTheme } from '~/constants/colors'

import '@chewy/kib-tag-styles/dist/kib-tag.css'
import '@chewy/kib-controls-styles/dist/styles/kib-button-new.css'
import '@chewy/kib-fields-new-styles/dist/styles/kib-field.css'
import styles from './RhapStatusSelect.module.scss'

export interface RhapStatusSelectProps<T> {
  colorMap: Record<string, ColorVariant>
  getOptionId: (option: T) => string
  getOptionName: (option: T) => string
  label: string
  onChange?: (newValue: string) => void
  options: T[]
  readOnly?: boolean
  value?: string
}

const RhapStatusSelect = <T,>({
  options,
  value,
  onChange,
  getOptionId,
  getOptionName,
  label,
  colorMap,
  readOnly = false,
}: RhapStatusSelectProps<T>) => {
  const selectedOption = options.find((o) => getOptionId(o) === value)
  const [open, setOpen] = useState(false)

  const ref = useRef(null)

  const handleSelect = (newValue: string) => {
    setOpen(false)
    if (onChange) {
      onChange(newValue)
    }
  }

  const getOptionTheme = (option: T) => {
    const optionName = getOptionName(option)
    return ColorVariantToTheme[colorMap[optionName] || ColorVariant.NORMAL]
  }

  useClickOutside(ref, () => {
    setOpen(false)
  })

  return (
    <div className={styles.container} ref={ref}>
      <KibField
        hiddenLabel
        label={label}
        renderControl={({ ...props }) => (
          <KibButtonNew
            {...(props as React.ButtonHTMLAttributes<HTMLButtonElement>)}
            className={classNames(
              styles.field,
              selectedOption && styles[getOptionTheme(selectedOption)],
              readOnly && styles.readOnly,
            )}
            icon={readOnly ? undefined : <KibChevronDownIcon />}
            iconPosition="end"
            size="small"
            type="button"
          >
            {selectedOption ? getOptionName(selectedOption) : 'Select...'}
          </KibButtonNew>
        )}
        value={selectedOption ? getOptionId(selectedOption) : ''}
        onClick={readOnly ? undefined : () => setOpen((prev) => !prev)}
      />

      {open && (
        <ul className={styles.menu} role="listbox">
          {options.map((option) => (
            <li
              aria-selected={value === getOptionId(option)}
              className={styles.item}
              key={getOptionId(option)}
              role="option"
              onClick={() => {
                handleSelect(getOptionId(option))
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  handleSelect(getOptionId(option))
                }
              }}
            >
              <KibTag theme={getOptionTheme(option) || 'default'}>
                {getOptionName(option)}
              </KibTag>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default RhapStatusSelect
